import { renewSession, supabase, SUPABASE_ANON_KEY, SUPABASE_URL } from "./supabase";
import { MFA_REQUIRED_CODE } from "../auth/mfa";
import type { VehicleMethod } from "@shared/vehicle.ts";

/** A failure the user can read: the server's message, or a plain one for network trouble. */
export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
  get offline() {
    return this.status === 0;
  }
}

async function token(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/** Sends with the session's token; a refused token is renewed once and the request made again. */
async function sendSigned(url: string, init: RequestInit & { headers: Record<string, string> }, signed = true): Promise<Response> {
  const attempt = async () => {
    const headers = { ...init.headers };
    if (signed) { const t = await token(); if (t) headers.Authorization = `Bearer ${t}`; }
    return send(url, { ...init, headers });
  };
  const res = await attempt();
  // A refused token while the session is still here (see renewSession): renewed once, and the call made again.
  if (res.status !== 401 || !signed || !(await renewSession())) return res;
  return attempt();
}

/** POST JSON to an edge function, with the session when there is one. */
export async function callFunction<T>(name: string, body: unknown, opts: { auth?: boolean } = {}): Promise<T> {
  const headers: Record<string, string> = { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" };
  return parse<T>(await sendSigned(`${SUPABASE_URL}/functions/v1/${name}`, { method: "POST", headers, body: JSON.stringify(body) }, opts.auth !== false));
}

/** Multipart POST (receipt images to /extract). */
export async function callFunctionForm<T>(name: string, form: FormData): Promise<T> {
  return parse<T>(await sendSigned(`${SUPABASE_URL}/functions/v1/${name}`, { method: "POST", headers: { apikey: SUPABASE_ANON_KEY }, body: form }));
}

/** What she reads when the event stream ended before the command's last event (streamFunction). */
export const STREAM_CUT = "The connection to Keept dropped before this command finished. Nothing was changed in QuickBooks, and anything it proposed is in the preview. Reload to see where it stopped, or try again.";

/**
 * POST JSON to a streaming edge function (text/event-stream) and hand each event to [onEvent] as it
 * arrives. Resolves when the stream ends; an error before the stream starts throws like callFunction.
 */
export async function streamFunction(name: string, body: unknown, onEvent: (event: string, data: unknown) => void, signal?: AbortSignal): Promise<void> {
  const headers: Record<string, string> = { apikey: SUPABASE_ANON_KEY, "Content-Type": "application/json" };
  const res = await sendSigned(`${SUPABASE_URL}/functions/v1/${name}`, { method: "POST", headers, body: JSON.stringify(body), signal });
  if (!res.ok || !res.body || !res.headers.get("content-type")?.includes("event-stream")) {
    await parse(res);
    throw new ApiError(res.status, "Unexpected answer from the server.");
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  // A stream that ends (or drops) before its last event: the server may have been stopped mid-command. Said plainly as an
  // error event, never a silent end — the command's own state is kept on the server for the reload.
  let ended = false;
  const endedEarly = () => { if (!ended && !signal?.aborted) onEvent("error", { message: STREAM_CUT, detail: "stream_cut" }); };
  for (;;) {
    let chunk: ReadableStreamReadResult<string>;
    try {
      chunk = await reader.read();
    } catch (e) {
      if (signal?.aborted) throw e;
      endedEarly();
      return;
    }
    const { value, done } = chunk;
    if (done) { endedEarly(); break; }
    buffer += value;
    for (let cut = buffer.indexOf("\n\n"); cut >= 0; cut = buffer.indexOf("\n\n")) {
      const block = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      const event = /^event: (.*)$/m.exec(block)?.[1];
      const data = /^data: (.*)$/m.exec(block)?.[1];
      if (!event || data === undefined) continue;
      if (event === "done" || event === "error") ended = true;
      try { onEvent(event, JSON.parse(data)); } catch { /* a malformed event is skipped */ }
    }
  }
}

async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    // A stopped stream (Stop) is the caller's own abort, not a lost connection.
    if (init.signal?.aborted) throw e;
    throw new ApiError(0, "You're offline or Keept's server can't be reached. Nothing was lost; try again.");
  }
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* not JSON */
  }
  if (!res.ok) {
    // Two-step verification was turned on (perhaps on another device) and this session hasn't passed the code: a
    // refreshed session carries the account's factors, and the app shows the code step.
    if (res.status === 403 && (body as { code?: string } | null)?.code === MFA_REQUIRED_CODE) void supabase.auth.refreshSession().catch(() => undefined);
    const message = (body as { error?: string } | null)?.error
      ?? (res.status === 401 ? "Your session ended. Sign in again." : `Something went wrong (${res.status}).`);
    throw new ApiError(res.status, message);
  }
  return body as T;
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong.";
}

// ---- Keept Runner engine (typed wrappers; the contract is in ENGINE-CONTRACT.md) ------------------------------------

const runner = <T>(action: string, body: Record<string, unknown> = {}) => callFunction<T>("runner", { action, ...body });

export interface UncatLine { lineId: string; amountCents: number; description: string; accountId: string; accountName: string }
export interface UncatPurchase {
  id: string; syncToken: string; date: string; payee: string | null; payeeId: string | null; totalCents: number;
  bankAccount: string | null; bankAccountId: string | null; paymentType: string | null; memo: string | null; docNumber: string | null; lines: UncatLine[];
}
export interface UncategorizedResult { accounts: { id: string; name: string }[]; since: string; read: number; pages: number; truncated: boolean; purchases: UncatPurchase[]; note?: string }

/** The company's expenses on its Uncategorized Expense (or Ask My Accountant) account since a date (default Jan 1), read from QuickBooks. Read-only. */
export const uncategorized = (clientId: string, since?: string) => runner<UncategorizedResult>("uncategorized", { clientId, ...(since ? { since } : {}) });

/** A preview item as the runner returns it (the fields the queue and the edit screens use). */
export interface RunnerItem {
  id: string; command_id: string | null; client_id: string | null; kind: string; title: string; summary: string;
  payload: Record<string, unknown>; proposed?: Record<string, unknown>; section: "ready" | "decide" | "flagged" | "matched"; status?: "pending" | "applied" | "rejected" | "failed";
  options: { label: string; why?: string; payload: Record<string, unknown> }[]; before: unknown; after: unknown; confidence?: number | null; model?: string | null;
  parked_at?: string | null; parked_by?: string | null; parent_id?: string | null; result?: Record<string, unknown> | null; error?: string | null; created_at?: string;
}
export interface ParkItem { id: string; payload?: Record<string, unknown>; option?: number }

/** "Send to review" (the side option): park items in Keept's review queue — nothing is sent to QuickBooks. */
export const park = (items: ParkItem[]) => runner<{ results: { id: string; status: "parked" | "failed"; parkedAt?: string; error?: string }[] }>("park", { items });
/** Back from the review queue to the preview. */
export const unpark = (ids: string[]) => runner<{ unparked: string[] }>("unpark", { ids });
/** Keept's review queue: parked items still waiting, newest first. Send = `send`, drop = `reject`, edit = `saveEdit`. */
export const reviewQueue = () => runner<{ items: RunnerItem[] }>("review");
/** Keep her edit of a waiting item (validated like the AI's), without sending it. */
export const saveEdit = (id: string, payload: Record<string, unknown>) => runner<{ proposal: RunnerItem }>("saveEdit", { id, payload });

/** The five fields of an edit after send; ids for category (account) and payee (vendor/customer). Amount in dollars (2 decimals). */
export interface FieldChanges { category?: string; categoryName?: string; amount?: number; date?: string; description?: string; payee?: string; payeeName?: string; lineId?: string }
export interface EditSentResult { proposal: RunnerItem; edited: { entity: string; id: string; before: Record<string, string>; after: Record<string, string> } }
/** Edit after send: re-open a sent item (its id) and change it; written to QuickBooks at once with the fresh SyncToken. */
export const editSent = (id: string, changes: FieldChanges) => runner<EditSentResult>("editSent", { id, changes });
/** The same for any QuickBooks record (not sent by Keept). */
export const editRecord = (clientId: string, entity: string, recordId: string, changes: FieldChanges) => runner<EditSentResult>("editSent", { clientId, entity, recordId, changes });

/** One QuickBooks company (a client of the office): the client picker lists these. */
export interface RunnerCompany { id: string; officeId: string; clientId: string | null; realmId: string; realmHash?: string; companyName: string; environment: string; status: string; lastSyncAt: string | null; connectedAt: string; sandbox?: boolean }
/** The office's clients (QuickBooks companies), for the client picker. */
export const companies = () => runner<{ companies: RunnerCompany[] }>("companies");

/**
 * Plan revision: the accountant's correction re-makes the waiting plan of the same command (event stream, like a plan:
 * start {revision}, text, plan {revised: true, revision, correction, scope}, await_confirm, done). Nothing runs until confirm.
 * `companyIds` (optional) changes the client scope at the same time.
 */
export const revisePlan = (commandId: string, correction: string, onEvent: (event: string, data: unknown) => void, opts: { companyIds?: string[]; signal?: AbortSignal } = {}) =>
  streamFunction("assistant", { revise: commandId, correction, ...(opts.companyIds ? { companyIds: opts.companyIds } : {}) }, onEvent, opts.signal);

/** What happened to an approved item, as the ledger tells it (after a crash, a reload, a lost answer). */
export interface RecoverItem { id: string; title: string; state: "written" | "written_now" | "not_written" | "failed" | "in_flight" | "unknown" | "rejected" | "waiting"; remoteId?: string | null; error?: string }
/** After a crash: the office's approved items whose write never reported back. `write: true` writes the ones that never reached QuickBooks (never twice). */
export const recoverWrites = (opts: { ids?: string[]; write?: boolean } = {}) => runner<{ items: RecoverItem[] }>("recover", { ...(opts.ids ? { ids: opts.ids } : {}), continue: opts.write === true });
/** Exactly what was and wasn't written, per item. */
export const runStatus = (ids: string[]) => runner<{ items: RecoverItem[] }>("runStatus", { ids });

export interface RunState { step: number; steps: number; lastTool: string | null; proposals: number; at: string }
/** A command's phase and its run's last step (after a reload); `stale` = it stopped mid-run (call recoverRuns). */
export const commandState = (commandId: string) => callFunction<{ commandId: string; phase: string; outcome: string; runState: RunState | null; heartbeatAt: string | null; reply: string; model: string | null; stale: boolean }>("assistant", { state: commandId });
/** Closes this seat's commands that stopped mid-work, each with a reply saying what it had done (also done automatically before every new command). */
export const recoverRuns = (commandId?: string) => callFunction<{ recovered: { commandId: string; reply: string; proposals: number }[] }>("assistant", { recover: commandId ?? true });

/** The connected company as QuickBooks names it now (CompanyInfo.CompanyName, via the OAuth session's realm); show "Sandbox" only as a small tag when `sandbox`. */
export interface ConnectedCompany { id: string; companyName: string; legalName: string | null; environment: string; sandbox: boolean; country: string | null; city: string | null; connectedAt: string; lastSyncAt: string | null }
export const connectedCompany = (clientId: string) => runner<{ company: ConnectedCompany }>("company", { clientId });

/** Vehicle methods: a client's method (standard mileage rate or actual expenses) for a tax year, as the runner lists it. */
export interface VehicleMethodRow extends Partial<VehicleFigures> { id: string; companyId: string | null; companyName: string; connected: boolean; year: number; method: VehicleMethod; updatedAt: string; /** Records Keept moved under the standard mileage rate that year and still moved (the method changed since). */ moved?: number }
/** The figures beside a method (the vehicle automat): miles per rate period, the mileage log's summary, business use,
 *  the account replaced costs move to, and what is known or still missing for the year. */
export interface VehicleFigures {
  businessMiles: number[] | null; businessUsePct: number | null; log: { trips: number; businessMiles: number; personalMiles: number; from: string | null; to: string | null; uploadedAt: string } | null;
  reclassAccount: string | null; periods: { from: string; to: string; rate: number }[]; rateNeeded: boolean; milesNeeded: boolean; pctNeeded: boolean;
  deductionCents: number | null; line: string; warning: string | null;
}
/** The one window: a client without a method for the year, with what its books suggest. */
export interface VehicleSetupClient { companyId: string; name: string; suggestion: VehicleMethod | null; evidence: string; targets: string[] }
export interface VehicleSetup { year: number; clients: VehicleSetupClient[]; /** Clients left out: no vehicle in their books; files as a partnership or corporation. */ noVehicle?: string[]; skipped?: { companyId: string; name: string; why: string }[]; prefs: { standardCosts: "reclassify" | "flag" | null }; rateKnown: boolean; periods: { from: string; to: string; rate: number }[] }
export const vehicleSetup = (year: number) => runner<VehicleSetup>("vehicleSetup", { year });
export const setVehicleMethods = (year: number, picks: { clientId: string; method: VehicleMethod | "skip" }[]) => runner<{ saved: { companyId: string; name: string; method: VehicleMethod; warnings: string[] }[] }>("setVehicleMethods", { year, picks });
export const setVehicleFacts = (clientId: string, year: number, facts: { miles?: number[]; businessUsePct?: number; log?: { trips: number; businessMiles: number; personalMiles: number; from: string | null; to: string | null }; account?: string }) => runner<{ method: VehicleMethodRow }>("setVehicleFacts", { clientId, year, ...facts });
export const setVehiclePrefs = (p: { standardCosts?: "reclassify" | "flag"; rate?: { year: number; cents: string } }) => runner<{ prefs: { standardCosts: "reclassify" | "flag" | null; rates: Record<string, number> } }>("setVehiclePrefs", p);
export const vehicleReclass = (clientId: string, year: number) => runner<{ mode: "reclassify" | "flag"; proposed: number; ids: string[]; guarded?: number }>("vehicleReclass", { clientId, year });
export const vehicleMethods = () => runner<{ methods: VehicleMethodRow[] }>("vehicleMethods");
/** After the method changed away from the standard mileage rate: the records Keept moved go back, as items in the preview. */
export const vehicleReverse = (clientId: string, year: number) => runner<{ proposed: number; ids: string[]; guarded?: number }>("vehicleReverse", { clientId, year });
/** Sets (or changes) one; `warning` is a plain note when it contradicts what was set (it is saved all the same). */
export const setVehicleMethod = (clientId: string, year: number, method: VehicleMethod) => runner<{ method: VehicleMethodRow; warning: string | null }>("setVehicleMethod", { clientId, year, method });
export const deleteVehicleMethod = (id: string) => runner<{ ok: true }>("deleteVehicleMethod", { id });
/** Every screen that shows vehicle methods reloads on this (a change in the tab, in Forms, or an answer to Keept's question). */
export const VEHICLE_METHODS_EVENT = "kr:vehicle-methods";
export const vehicleMethodsChanged = () => { try { window.dispatchEvent(new Event(VEHICLE_METHODS_EVENT)); } catch { /* no window */ } };
